// src/components/ProtectedRoute.js
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, addDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase/firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { Loader, Shield, Lock, AlertTriangle } from 'lucide-react';

const ProtectedRoute = ({ 
  children, 
  requireAuth = false,
  requireAdmin = false 
}) => {
  const { isAuthenticated, user, userProfile, loading: authLoading, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [adminCheck, setAdminCheck] = useState({
    isFirebaseAdmin: false,
    isFirestoreAdmin: false,
    hasAccess: false,
    userType: 'unknown'
  });
  const location = useLocation();

  // פונקציה לרישום אזהרת אבטחה
  const logSecurityWarning = async (user, userProfile, attemptDetails) => {
    try {
   
      const securityLog = {
        // פרטי המשתמש
        userId: user?.uid || 'unknown',
        userEmail: user?.email || 'unknown',
        userDisplayName: user?.displayName || userProfile?.displayName || 'unknown',
        
        // פרטי הפרופיל
        profileExists: !!userProfile,
        isAdminInProfile: userProfile?.isAdmin || false,
        userFirstName: userProfile?.firstName || 'unknown',
        userLastName: userProfile?.lastName || 'unknown',
        userPhone: userProfile?.phone || 'unknown',
        userCreatedAt: userProfile?.createdAt || 'unknown',
        
        // פרטי הניסיון
        attemptedRoute: location.pathname,
        attemptedAction: 'admin_access',
        attemptTime: new Date().toISOString(),
        attemptTimeLocal: new Date().toLocaleString('he-IL'),
        
        // פרטי הדפדפן והרשת
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct',
        origin: window.location.origin,
        
        // פרטי האבטחה
        securityLevel: 'HIGH',
        threatType: 'UNAUTHORIZED_ADMIN_ACCESS',
        blocked: true,
        
        // נתונים נוספים
        sessionData: {
          isAuthenticated: isAuthenticated,
          hasUserProfile: !!userProfile,
          authMethod: userProfile ? 'firestore' : 'firebase_only',
          ...attemptDetails
        }
      };
      
      // שמירה בקולקציית אזהרות
      await addDoc(collection(db, 'security_warnings'), securityLog);
      
   
    } catch (error) {
      console.error('❌ Failed to log security warning:', error);
      
      // במקרה של שגיאה בשמירה, נבצע log לוקלי
      console.warn('📝 Local security log (Firestore failed):', {
        user: user?.email,
        time: new Date().toISOString(),
        route: location.pathname,
        error: error.message
      });
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
   
      if (authLoading) {
     
        return;
      }

      if (requireAdmin && user) {
      
        try {
          // בדיקה 1: Firebase Auth אדמין ישן (ללא Firestore)
          const isFirebaseAdmin = auth.currentUser !== null && !userProfile;
         
          // בדיקה 2: Firestore אדמין חדש
          let isFirestoreAdmin = false;
          let manualUserData = null;
          
          if (userProfile) {
            // יש פרופיל מהקונטקסט
            isFirestoreAdmin = userProfile.isAdmin === true;
            
          } else {
            // אין פרופיל מהקונטקסט - נסה לטעון ידנית
     
            try {
              const userDoc = await getDoc(doc(db, 'users', user.uid));
              if (userDoc.exists()) {
                manualUserData = userDoc.data();
                isFirestoreAdmin = manualUserData.isAdmin === true;
            
              } else {
                console.log('   - No Firestore document found');
              }
            } catch (firestoreError) {
              console.error('   - Error loading Firestore profile:', firestoreError);
            }
          }
          
          // בדיקה 3: בדיקה נוספת דרך הקונטקסט
          let contextAdmin = false;
          try {
            contextAdmin = typeof isAdmin === 'function' ? isAdmin() : isAdmin;
           
          } catch (error) {
            console.log('   - Context Admin error:', error);
          }
          
          // החלטה סופית
          const hasAccess = isFirebaseAdmin || (isFirestoreAdmin === true);
          
          // 🚫 בדיקה חדה: אם יש פרופיל ו-isAdmin=false, דחה בכל מקרה
          if ((userProfile && userProfile.isAdmin === false) || 
              (manualUserData && manualUserData.isAdmin === false)) {
            
            
            
            // 🚨 רישום אזהרת אבטחה
            await logSecurityWarning(user, userProfile || manualUserData, {
              checkType: 'explicit_denial',
              isFirebaseAdmin,
              isFirestoreAdmin,
              contextAdmin,
              hasAccess: false,
              denialReason: 'isAdmin_false_in_profile'
            });
            
            setAdminCheck({
              isFirebaseAdmin: false,
              isFirestoreAdmin: false,
              hasAccess: false,
              userType: 'regular-user'
            });
            
          } else if (!hasAccess) {
          
            // 🚨 רישום אזהרת אבטחה
            await logSecurityWarning(user, userProfile || manualUserData, {
              checkType: 'no_admin_permissions',
              isFirebaseAdmin,
              isFirestoreAdmin,
              contextAdmin,
              hasAccess: false,
              denialReason: 'no_admin_permissions_found'
            });
            
            setAdminCheck({
              isFirebaseAdmin,
              isFirestoreAdmin,
              hasAccess: false,
              userType: 'unauthorized'
            });
            
          } else {
            const userType = isFirebaseAdmin ? 'firebase-admin' : 
                            isFirestoreAdmin ? 'firestore-admin' : 'unknown';
            
         
            // רישום כניסה מוצלחת (לא אזהרה)
            try {
              await addDoc(collection(db, 'admin_access_logs'), {
                userId: user.uid,
                userEmail: user.email,
                userType,
                accessTime: new Date().toISOString(),
                route: location.pathname,
                success: true
              });
              console.log('📝 Successful admin access logged');
            } catch (logError) {
              console.warn('Failed to log successful access:', logError);
            }
            
            setAdminCheck({
              isFirebaseAdmin,
              isFirestoreAdmin,
              hasAccess: true,
              userType
            });
          }
          
          console.log('🔍 ProtectedRoute Final Decision:');
          console.log('   - isFirebaseAdmin:', isFirebaseAdmin);
          console.log('   - isFirestoreAdmin:', isFirestoreAdmin);
          console.log('   - hasAccess:', hasAccess);
          console.log('   - userProfile?.isAdmin:', userProfile?.isAdmin);
          
        } catch (error) {
          console.error('❌ ProtectedRoute: Error checking admin status:', error);
          
          // רישום שגיאה
          await logSecurityWarning(user, userProfile, {
            checkType: 'error',
            error: error.message,
            denialReason: 'system_error'
          });
          
          setAdminCheck({
            isFirebaseAdmin: false,
            isFirestoreAdmin: false,
            hasAccess: false,
            userType: 'error'
          });
        }
      } else {
        console.log('✅ ProtectedRoute: No admin check required');
        setAdminCheck({
          isFirebaseAdmin: false,
          isFirestoreAdmin: false,
          hasAccess: true, // לא דרוש אדמין
          userType: 'regular-user'
        });
      }
      
      setLoading(false);
    };

    checkAuth();
  }, [user, userProfile, requireAdmin, authLoading, isAuthenticated, location.pathname]);

  // מסך טעינה
  if (loading || authLoading) {
    console.log('⏳ ProtectedRoute: Showing loading screen');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              {requireAdmin ? (
                <Shield className="w-8 h-8 text-white" />
              ) : (
                <Lock className="w-8 h-8 text-white" />
              )}
            </div>
            <Loader className="w-6 h-6 animate-spin text-blue-500 absolute top-2 right-2" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {requireAdmin ? 'בודק הרשאות מנהל...' : 'בודק הרשאות...'}
          </h3>
          <p className="text-gray-600">אנא המתן...</p>
        </div>
      </div>
    );
  }

  // בדיקת הרשאות אדמין
  if (requireAdmin) {
    console.log('🔐 ProtectedRoute: Checking admin access...');
    console.log('   - adminCheck:', adminCheck);
    
    if (!adminCheck.hasAccess) {
      console.log('🚫 ProtectedRoute: ACCESS DENIED - Showing blocked screen');
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">גישה מוגבלת</h3>
            <p className="text-gray-600 mb-4">
              עמוד זה מיועד למנהלי המערכת בלבד. אין לך הרשאות מתאימות לצפייה בתוכן זה.
            </p>
            
            {/* הצגת פרטי המשתמש לצורך דיבוג */}
            <div className="bg-gray-100 rounded-lg p-4 mb-6 text-sm text-left">
              <p><strong>אימייל:</strong> {user?.email}</p>
              <p><strong>סוג משתמש:</strong> {adminCheck.userType}</p>
              <p><strong>הרשאות אדמין:</strong> {userProfile?.isAdmin ? 'כן' : 'לא'}</p>
              <p className="text-red-600 mt-2"><strong>⚠️ ניסיון הגישה נרשם במערכת</strong></p>
            </div>
            
            <div className="space-y-3">
              <a 
                href="/admin/login"
                className="block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                התחברות מנהל
              </a>
              <a 
                href="/"
                className="block bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors"
              >
                חזרה לדף הבית
              </a>
            </div>
          </div>
        </div>
      );
    } else {
      console.log('✅ ProtectedRoute: ACCESS GRANTED - User has admin permissions');
    }
  }

  // בדיקת הרשאת משתמש רגיל
  if (requireAuth && !isAuthenticated) {
    
    return (
      <Navigate 
        to="/auth/login" 
        state={{ from: location }} 
        replace 
      />
    );
  }


  return children;
};

export default ProtectedRoute;